import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Sparkles, LayoutDashboard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import SaferSavvyLogo from "./SaferSavvyLogo";
import ThemeToggle from "./common/ThemeToggle";
import { useTheme } from "../contexts/ThemeContext";

const goToLogin = () => { window.location.href = '/login'; };

/* Anchor links — these scroll within the home page (sections that still
   live on Home). When clicked from a sub-page we navigate to "/#section".
   Dropdown items collect related sub-routes under a single nav label. */
type AnchorItem  = { kind: "anchor"; label: string; hash: string };
type RouteItem   = { kind: "route"; label: string; to: string };
type DropdownChild = {
  to: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
};
type DropdownItem = { kind: "dropdown"; label: string; children: DropdownChild[] };
type NavItem = AnchorItem | RouteItem | DropdownItem;

const navItems: NavItem[] = [
  { kind: "anchor",   label: "Home",         hash: "home" },
  { kind: "anchor",   label: "Features",     hash: "features" },
  {
    kind: "dropdown",
    label: "Explore",
    children: [
      {
        to: "/why-safersavvy",
        label: "Why SaferSavvy",
        description: "Three reasons your campus commute just got better.",
        icon: Sparkles,
      },
      {
        to: "/transport-dashboard",
        label: "Dashboard",
        description: "Smart transportation dashboard preview.",
        icon: LayoutDashboard,
      },
    ],
  },
  { kind: "anchor", label: "How It Works", hash: "how-it-works" },
  { kind: "anchor", label: "Testimonials", hash: "testimonials" },
  { kind: "anchor", label: "Contact",      hash: "contact" },
];

const Navbar: React.FC = React.memo(() => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  /* Tracks which dropdown is open by label (only one at a time). */
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  /* Mobile: expanded state for collapsible dropdowns */
  const [mobileExpanded, setMobileExpanded] = useState<Set<string>>(new Set());
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const location = useLocation();

  /* On non-Home routes there is no transparent hero behind the navbar, so
     force the "scrolled" opaque style the moment the page loads. */
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          setIsScrolled(window.scrollY > 50);
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu when clicking outside its container
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isMenuOpen &&
        !(event.target as Element).closest(".mobile-menu-container")
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isMenuOpen]);

  /* Close desktop dropdown on outside-click and on ESC. */
  useEffect(() => {
    if (!openDropdown) return;
    const onClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpenDropdown(null);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [openDropdown]);

  /* Close any open dropdown when the route changes (post-navigation). */
  useEffect(() => {
    setOpenDropdown(null);
  }, [location.pathname]);

  /* On sub-pages, treat as scrolled (opaque) regardless of scroll position. */
  const showAsScrolled = isScrolled || !isHomePage;

  /* Choose backgrounds: in dark mode the "scrolled" state uses the ink panel
     so the navbar sits on the same dark surface as the body. */
  const headerBg = showAsScrolled
    ? isDark
      ? "bg-ink-850/90 backdrop-blur-xl border-b border-ink-700/70 shadow-[0_1px_0_rgba(255,255,255,0.04)] py-3"
      : "bg-white shadow-md py-3"
    : "bg-transparent py-5";

  /* Top-of-page (transparent) link colour:
     - light theme: white (over the navy hero)
     - dark theme: light ink (over the dark hero) */
  const topNavLinkColor = isDark ? "text-ink-100" : "text-white";
  const scrolledNavLinkColor = isDark ? "text-ink-100" : "text-primary-500";
  const navLinkColor = showAsScrolled ? scrolledNavLinkColor : topNavLinkColor;

  /* Underline colour on hover */
  const underlineColor = showAsScrolled
    ? isDark ? "bg-accent-500" : "bg-blue-500"
    : "bg-yellow-400";

  /* For an anchor item: if we are on Home, scroll within page; otherwise
     navigate back to "/#section" so the browser scrolls after route load. */
  const renderAnchorHref = (hash: string) =>
    isHomePage ? `#${hash}` : `/#${hash}`;

  /* Active highlight for route items */
  const isRouteActive = (to: string) => location.pathname === to;
  /* Dropdown is "active" if any of its children matches the current route */
  const isDropdownActive = (children: DropdownChild[]) =>
    children.some((c) => isRouteActive(c.to));

  /* Toggle a mobile dropdown's expanded state */
  const toggleMobileExpanded = (label: string) => {
    setMobileExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  return (
    <>
    <header
      className={`fixed w-full top-0 z-50 transition-all duration-300 px-4 sm:px-6 ${headerBg}`}
    >
      <div className="max-w-screen-xl mx-auto flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <SaferSavvyLogo
            size={showAsScrolled ? 60 : 70}
            className="transition-all duration-300"
          />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden lg:flex items-center space-x-5 xl:space-x-8">
          {navItems.map((item) => {
            const baseClass = `font-medium text-sm xl:text-base transition-colors duration-200 relative group ${navLinkColor}`;

            if (item.kind === "dropdown") {
              const active = isDropdownActive(item.children);
              const open = openDropdown === item.label;
              return (
                <div
                  key={item.label}
                  className="relative"
                  ref={open ? dropdownRef : undefined}
                >
                  <button
                    type="button"
                    onClick={() => setOpenDropdown(open ? null : item.label)}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    className={`inline-flex items-center gap-1 ${baseClass} ${active ? "!text-accent-500" : ""}`}
                  >
                    {item.label}
                    <ChevronDown
                      className={`w-3.5 h-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                    />
                    <span
                      className={`absolute left-0 -bottom-1 h-0.5 transition-all duration-300 ${
                        active ? "w-full bg-accent-500" : `w-0 group-hover:w-full ${underlineColor}`
                      }`}
                    />
                  </button>

                  <AnimatePresence>
                    {open && (
                      <motion.div
                        initial={{ opacity: 0, y: -8, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: -8, scale: 0.96 }}
                        transition={{ duration: 0.15 }}
                        className="absolute right-0 mt-3 w-72 z-50 rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden dark:border-ink-700 dark:bg-ink-800"
                        role="menu"
                      >
                        <div className="px-4 py-3 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white dark:border-ink-700 dark:bg-none dark:bg-ink-850">
                          <p className="text-xs font-bold uppercase tracking-wider text-slate-500 dark:text-ink-300">
                            Explore SaferSavvy
                          </p>
                          <p className="text-[11px] text-slate-500 dark:text-ink-400 mt-0.5">
                            Dive deeper into what makes the platform tick.
                          </p>
                        </div>
                        <ul className="py-1.5">
                          {item.children.map(({ to, label, description, icon: Icon }) => {
                            const childActive = isRouteActive(to);
                            return (
                              <li key={to}>
                                <a
                                  href={to}
                                  onClick={() => setOpenDropdown(null)}
                                  className={`flex items-start gap-3 px-3 py-2.5 mx-1.5 my-0.5 rounded-xl transition-colors group/child ${
                                    childActive
                                      ? "bg-accent-50 dark:bg-accent-500/15"
                                      : "hover:bg-slate-50 dark:hover:bg-ink-700/60"
                                  }`}
                                >
                                  <div
                                    className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${
                                      childActive
                                        ? "bg-accent-500 text-white"
                                        : "bg-primary-50 text-primary-600 dark:bg-primary-600/20 dark:text-primary-400"
                                    }`}
                                  >
                                    <Icon className="w-4 h-4" />
                                  </div>
                                  <div className="min-w-0">
                                    <p className={`text-sm font-semibold leading-tight ${
                                      childActive
                                        ? "text-accent-700 dark:text-accent-300"
                                        : "text-slate-800 dark:text-ink-100"
                                    }`}>
                                      {label}
                                    </p>
                                    <p className="text-[11px] text-slate-500 dark:text-ink-300 mt-0.5 leading-snug">
                                      {description}
                                    </p>
                                  </div>
                                </a>
                              </li>
                            );
                          })}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }

            if (item.kind === "route") {
              const active = isRouteActive(item.to);
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  className={`${baseClass} ${active ? "!text-accent-500" : ""}`}
                >
                  {item.label}
                  <span
                    className={`absolute left-0 -bottom-1 h-0.5 transition-all duration-300 ${
                      active ? "w-full bg-accent-500" : `w-0 group-hover:w-full ${underlineColor}`
                    }`}
                  />
                </Link>
              );
            }

            return (
              <a
                key={item.label}
                href={renderAnchorHref(item.hash)}
                className={baseClass}
              >
                {item.label}
                <span
                  className={`absolute left-0 -bottom-1 h-0.5 w-0 transition-all duration-300 group-hover:w-full ${underlineColor}`}
                />
              </a>
            );
          })}
        </nav>

        {/* Right cluster: theme toggle + login */}
        <div className="hidden lg:flex items-center gap-3">
          <ThemeToggle
            className={`${
              showAsScrolled
                ? isDark
                  ? "!text-ink-100 hover:!bg-ink-700"
                  : "!text-primary-500 hover:!bg-slate-100"
                : "!text-white hover:!bg-white/10"
            }`}
          />

          <button
            onClick={goToLogin}
            className={`px-6 xl:px-8 py-2 rounded-full font-medium transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-1 ${
              showAsScrolled
                ? isDark
                  ? "bg-primary-600 hover:bg-primary-500 text-white"
                  : "bg-primary-500 hover:bg-primary-700 text-white"
                : "bg-accent-600 hover:bg-accent-700 text-white"
            }`}
          >
            Login
          </button>
        </div>

        {/* Mobile right cluster: theme toggle + hamburger */}
        <div className="lg:hidden flex items-center gap-2">
          <ThemeToggle
            size="sm"
            className={
              showAsScrolled
                ? isDark
                  ? "!text-ink-100 hover:!bg-ink-700"
                  : "!text-gray-800 hover:!bg-slate-100"
                : "!text-white hover:!bg-white/10"
            }
          />

          {!isMenuOpen && (
            <button
              className="focus:outline-none z-50 p-2 rounded-lg"
              onClick={() => setIsMenuOpen(true)}
              aria-label="Open menu"
            >
              <Menu
                size={28}
                className={
                  showAsScrolled
                    ? isDark ? "text-ink-100" : "text-gray-800"
                    : "text-white"
                }
              />
            </button>
          )}
        </div>




      </div>
    </header>

    {/* Mobile Menu Overlay — OUTSIDE header to escape its stacking context */}
    {isMenuOpen && (
      <div
        className="fixed inset-0 bg-black/50 dark:bg-black/70 z-[60] lg:hidden"
        onClick={() => setIsMenuOpen(false)}
      />
    )}

    {/* Mobile Side Menu — OUTSIDE header to escape its stacking context */}
    <div
      className={`mobile-menu-container fixed top-0 right-0 h-screen w-3/4 max-w-sm bg-white dark:bg-ink-850 shadow-xl z-[70] transform transition-transform duration-300 ease-in-out lg:hidden ${
        isMenuOpen ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <div className="h-full flex flex-col">
        {/* Menu Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-100 dark:border-ink-700/70">
          <Link to="/" className="flex items-center" onClick={() => setIsMenuOpen(false)}>
            <SaferSavvyLogo size={64} />
          </Link>
          <button
            onClick={() => setIsMenuOpen(false)}
            className="text-gray-500 hover:text-gray-700 dark:text-ink-300 dark:hover:text-ink-50"
            aria-label="Close menu"
          >
            <X size={24} />
          </button>
        </div>

        {/* Menu Items */}
        <nav className="flex-1 overflow-y-auto p-6 space-y-2">
          {navItems.map((item) => {
            const baseLink =
              "block py-3 px-4 rounded-lg text-gray-700 hover:bg-blue-50 hover:text-primary-600 dark:text-ink-200 dark:hover:bg-ink-700/70 dark:hover:text-ink-50 transition-colors duration-200 font-medium";

            if (item.kind === "dropdown") {
              const expanded = mobileExpanded.has(item.label);
              const active = isDropdownActive(item.children);
              return (
                <div key={item.label}>
                  <button
                    type="button"
                    onClick={() => toggleMobileExpanded(item.label)}
                    className={`flex items-center justify-between w-full ${baseLink} ${
                      active ? "!text-accent-500 dark:!text-accent-400 bg-accent-50/60 dark:bg-accent-500/10" : ""
                    }`}
                  >
                    <span>{item.label}</span>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${expanded ? "rotate-180" : ""}`}
                    />
                  </button>
                  <AnimatePresence initial={false}>
                    {expanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.22, ease: [0.4, 0, 0.2, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="pl-3 mt-1 mb-1 space-y-0.5 border-l-2 border-slate-200 dark:border-ink-700/80 ml-4">
                          {item.children.map(({ to, label, description, icon: Icon }) => {
                            const childActive = isRouteActive(to);
                            return (
                              <a
                                key={to}
                                href={to}
                                onClick={() => setIsMenuOpen(false)}
                                className={`flex items-start gap-2.5 py-2.5 px-3 rounded-lg transition-colors ${
                                  childActive
                                    ? "bg-accent-50 dark:bg-accent-500/15 text-accent-700 dark:text-accent-300"
                                    : "text-gray-700 dark:text-ink-200 hover:bg-slate-50 dark:hover:bg-ink-700/60"
                                }`}
                              >
                                <Icon className="w-4 h-4 shrink-0 mt-0.5" />
                                <div className="min-w-0">
                                  <p className="text-sm font-semibold leading-tight">{label}</p>
                                  <p className="text-[11px] opacity-75 mt-0.5 leading-snug">{description}</p>
                                </div>
                              </a>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            }

            if (item.kind === "route") {
              const active = isRouteActive(item.to);
              return (
                <Link
                  key={item.label}
                  to={item.to}
                  onClick={() => setIsMenuOpen(false)}
                  className={`${baseLink} ${active ? "!text-accent-500 dark:!text-accent-400 bg-accent-50/60 dark:bg-accent-500/10" : ""}`}
                >
                  {item.label}
                </Link>
              );
            }
            return (
              <a
                key={item.label}
                href={renderAnchorHref(item.hash)}
                className={baseLink}
                onClick={() => setIsMenuOpen(false)}
              >
                {item.label}
              </a>
            );
          })}
        </nav>

        {/* Menu Footer */}
        <div className="p-6 border-t border-gray-100 dark:border-ink-700/70">
          <button
            onClick={() => { setIsMenuOpen(false); goToLogin(); }}
            className="block w-full text-center bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-all duration-300 shadow-md"
          >
            Login
          </button>
        </div>
      </div>
    </div>
    </>
  );
});

Navbar.displayName = 'Navbar';
export default Navbar;
